import { Box, CircularProgress, Typography } from "@mui/material";
import moment from "moment";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { getDSNames } from "./scenarioApprovalUtils";
import {
	mapCustomerGroupModifications,
	mapCustomerModifications,
	mapProductGroupModifications,
	mapSKUModifications,
} from "./scenarioApprovalUtils";
import CollapseSection from "../../../components/ui/CollapseSection";
import ModificationEntityList from "./ModificationEntityList";

function pickField(obj, ...keys) {
	for (const k of keys) {
		if (obj != null && obj[k] !== undefined && obj[k] !== null && obj[k] !== "") {
			return obj[k];
		}
	}
	return "";
}

export default function ScenarioDetails() {
	const state = useSelector((store) => store.scenarioApproval);
	const {
		scenarioDetails,
		countries,
		Scenario_Details,
		ScPGModificationDetails,
		ScSKUModificationDetails,
		ScCGModificationDetails,
		ScCustModificationDetails,
		selectedScenarioModificationID,
		modificationDetailsLoading,
	} = state;

	const modificationKey = selectedScenarioModificationID ?? "default";

	const productGroupItems = useMemo(
		() => mapProductGroupModifications(ScPGModificationDetails),
		[ScPGModificationDetails]
	);
	const skuItems = useMemo(
		() => mapSKUModifications(ScSKUModificationDetails),
		[ScSKUModificationDetails]
	);
	const customerGroupItems = useMemo(
		() => mapCustomerGroupModifications(ScCGModificationDetails),
		[ScCGModificationDetails]
	);
	const customerItems = useMemo(
		() => mapCustomerModifications(ScCustModificationDetails),
		[ScCustModificationDetails]
	);

	if (scenarioDetails === null) {
		return null;
	}

	const {
		scenarioName,
		effectiveStartDate,
		effectiveEndDate,
		scenarioDatasource,
		countryID,
		customerBreakdownType,
		customerBreakdown,
		skuBreakdown,
	} = scenarioDetails;

	const dsNames = getDSNames(scenarioDatasource);
	const country = countries.find((p) => p.countryID == countryID);
	const selectedCountryName = country?.countryName ?? "";

	const scenarioDetailRow = Array.isArray(Scenario_Details)
		? Scenario_Details[0]
		: null;
	const displayScenarioName =
		pickField(scenarioDetailRow, "scenarioName", "ScenarioName") || scenarioName;
	const displayGeo =
		pickField(scenarioDetailRow, "geoName", "GeoName") || selectedCountryName;

	const startDate =
		pickField(scenarioDetailRow, "effectiveStartDate", "EffectiveStartDate") ||
		effectiveStartDate;
	const endDate =
		pickField(scenarioDetailRow, "effectiveEndDate", "EffectiveEndDate") ||
		effectiveEndDate;

	const period =
		startDate && endDate
			? `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`
			: "";

	return (
		<Box sx={{ mt: 2 }}>
			<CollapseSection title="Scenario Details">
				<Box sx={{ fontSize: "14px", ml: 2, mt: 2 }}>
					<LabelValue label="Scenario Of" value={displayScenarioName} />
					<LabelValue label="Scenario Name" value={displayScenarioName} />
					<LabelValue label="Country" value={displayGeo} />
					<LabelValue label="Validity Period" value={period} />

					<Box sx={{ position: "relative" }}>
						{modificationDetailsLoading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: 80,
									mb: 2,
								}}
							>
								<CircularProgress size={28} />
							</Box>
						) : (
							<>
								<ModificationEntityList
									key={`pg-${modificationKey}`}
									label="Product Group(s):"
									items={productGroupItems}
									addedLabel="Added Product Groups"
									deletedLabel="Deleted Product Groups"
									hideSortByNumber
								/>
								<ModificationEntityList
									key={`sku-${modificationKey}`}
									label="SKU(s):"
									items={skuItems}
									addedLabel="Added SKUs"
									deletedLabel="Deleted SKUs"
								/>
								<ModificationEntityList
									key={`cg-${modificationKey}`}
									label="Customer Group(s):"
									items={customerGroupItems}
									addedLabel="Added Customer Groups"
									deletedLabel="Deleted Customer Groups"
									hideSortByNumber
								/>
								<ModificationEntityList
									key={`cust-${modificationKey}`}
									label="Customer(s):"
									items={customerItems}
									addedLabel="Added Customers"
									deletedLabel="Deleted Customers"
								/>
							</>
						)}
					</Box>

					<Box>
						<Typography
							sx={{ fontWeight: "600", fontSize: "14px" }}
							display="inline"
						>
							Data Source(s):{" "}
						</Typography>
						<Typography display="inline" sx={{ fontSize: "14px" }}>
							{dsNames.join(", ")}
						</Typography>
					</Box>
					<Box>
						<Typography
							sx={{ fontWeight: "600", fontSize: "14px" }}
							display="inline"
						>
							Customer breakdown methodology (s):{" "}
						</Typography>
						<Typography display="inline" sx={{ fontSize: "14px" }}>
							{customerBreakdownType} {customerBreakdown}
						</Typography>
					</Box>
					<Box mb={3}>
						<Typography
							sx={{ fontWeight: "600", fontSize: "14px" }}
							display="inline"
						>
							SKU breakdown methodology (s):{" "}
						</Typography>
						<Typography display="inline" sx={{ fontSize: "14px" }}>
							{skuBreakdown}
						</Typography>
						{skuBreakdown !== undefined &&
							skuBreakdown !== "" &&
							skuBreakdown !== "External" && (
								<Typography display="inline"> {customerBreakdown}</Typography>
							)}
					</Box>
				</Box>
			</CollapseSection>
		</Box>
	);
}

function LabelValue({ label, value }) {
	return (
		<div>
			<strong>{label}:</strong>&nbsp;{value}
		</div>
	);
}
